import { useState, useEffect, useCallback } from 'react';
import { adminAPI, getAuthToken } from '../../lib/api';
import { 
  Users, 
  Plus,
  PencilSimple,
  Trash,
  X,
  Check,
  UserCircle,
  FirstAidKit,
  Stethoscope,
  ShieldCheck,
  MagnifyingGlass
} from '@phosphor-icons/react';

const roles = [
  { value: 'patient', label: 'Patient', icon: UserCircle },
  { value: 'cma', label: 'CMA', icon: FirstAidKit },
  { value: 'provider', label: 'Provider', icon: Stethoscope },
  { value: 'admin', label: 'Admin', icon: ShieldCheck },
];

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'patient',
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await adminAPI.getUsers();
      setUsers(res.data);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = useCallback(() => {
    let result = [...users];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(u => 
        u.email.toLowerCase().includes(query) ||
        u.first_name.toLowerCase().includes(query) ||
        u.last_name.toLowerCase().includes(query)
      );
    }
    
    if (filterRole !== 'all') {
      result = result.filter(u => u.role === filterRole);
    }
    
    setFilteredUsers(result);
  }, [users, searchQuery, filterRole]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    
    try {
      if (editingUser) {
        // Update existing user
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${editingUser.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            first_name: formData.first_name,
            last_name: formData.last_name,
            role: formData.role,
          }),
        });
      } else {
        // Create new user
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify(formData),
        });
      }
      
      setShowModal(false);
      resetForm();
      loadUsers();
    } catch (error) {
      setError('Failed to save user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
      });
      setDeleteConfirm(null);
      loadUsers();
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error deleting user:', error);
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'patient',
    });
    setError('');
  };

  const getRoleIcon = (role) => {
    const r = roles.find(r => r.value === role);
    return r ? r.icon : UserCircle;
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'patient': return 'bg-blue-100 text-blue-700';
      case 'cma': return 'bg-teal-100 text-teal-700';
      case 'provider': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage system users and permissions</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-teal flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <Plus size={18} weight="bold" />
          Add User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex-1 min-w-[250px] relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="input-clinical pl-10 w-full"
            data-testid="user-search"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="input-clinical w-40"
          data-testid="role-filter"
        >
          <option value="all">All Roles</option>
          {roles.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{users.length}</div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Total</div>
        </div>
        {roles.map(r => (
          <div key={r.value} className="bg-white border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-slate-900">
              {users.filter(u => u.role === r.value).length}
            </div>
            <div className="text-xs text-slate-500 uppercase tracking-wider">{r.label}s</div>
          </div>
        ))}
      </div>

      {/* Users Table */}
      <div className="card-clinical">
        <div className="overflow-x-auto">
          <table className="table-clinical" data-testid="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-12 text-slate-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const Icon = getRoleIcon(user.role);
                  return (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getRoleBadgeColor(user.role).split(' ')[0]}`}>
                            <Icon size={16} className={getRoleBadgeColor(user.role).split(' ')[1]} />
                          </div>
                          <span className="font-medium text-slate-900">
                            {user.first_name} {user.last_name}
                          </span>
                        </div>
                      </td>
                      <td className="text-slate-600">{user.email}</td>
                      <td>
                        <span className={`status-badge ${getRoleBadgeColor(user.role)}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="text-slate-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(user)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full" data-testid="user-modal">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">
                  {editingUser ? 'Edit User' : 'Add New User'}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="text-slate-400 hover:text-slate-600">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">First Name</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="input-clinical"
                    required
                    data-testid="user-first-name"
                  />
                </div>
                <div>
                  <label className="label-clinical">Last Name</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="input-clinical"
                    required
                    data-testid="user-last-name"
                  />
                </div>
              </div>

              {!editingUser && (
                <>
                  <div>
                    <label className="label-clinical">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-clinical"
                      required
                      data-testid="user-email"
                    />
                  </div>
                  <div>
                    <label className="label-clinical">Password</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-clinical"
                      required
                      data-testid="user-password"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="label-clinical">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input-clinical"
                  data-testid="user-role"
                >
                  {roles.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 btn-teal flex items-center justify-center gap-2"
                  data-testid="save-user-btn"
                >
                  {saving ? <div className="spinner" /> : (
                    <>
                      <Check size={18} weight="bold" />
                      {editingUser ? 'Update' : 'Create'} User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Delete User?</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-full transition-colors"
                data-testid="confirm-delete-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
